import {
  getRequiredObjectId,
  siteAssignmentStrategies,
  SiteEntity,
  toSiteAssignmentStrategy
} from "@ots-frontend/ots-data";
import {useTranslation} from "react-i18next";
import {ActionIcon, Button, Center, Divider, Grid, Group, Text} from "@mantine/core";
import {useCallback, useMemo} from "react";
import {
  SiteAssignmentStrategySelect
} from "../../../select/entity/SiteAssignmentStrategySelect/SiteAssignmentStrategySelect";
import {DragDropContext, Draggable, Droppable} from 'react-beautiful-dnd';
import {IconGripVertical, IconTrash} from "@tabler/icons-react";
import {SiteSelect, SiteSelectModal} from "../../../select/entity/SiteSelect/SiteSelect";
import {useDisclosure} from "@mantine/hooks";
import {
  SiteAssignmentSiteAssocFormValues,
  SiteSiteAssignmentFormFieldValues,
  SiteSiteAssignmentFormProvider,
  SiteSiteAssignmentFormValues,
  useSiteSiteAssignmentForm,
  useSiteSiteAssignmentFormContext
} from "./form-context";

interface SiteSiteAssignmentFormProps {
  defaultValues?: Partial<SiteSiteAssignmentFormValues>;
  onSubmit?: (values: SiteSiteAssignmentFormValues) => void;
  submitting?: boolean;
}

export const getDefaultSiteSiteAssignmentFormValues = (defaultValues: Partial<SiteSiteAssignmentFormValues> | undefined) => {
  return {
    strategy: defaultValues?.strategy?.code ?? siteAssignmentStrategies.ranked.code,
    normalSites: defaultValues?.assocs?.sort((a, b) => a.normalRank - b.normalRank).map((assoc) => ({
      id: getRequiredObjectId(assoc.site),
    })) ?? [],
    serviceSites: defaultValues?.assocs?.sort((a, b) => a.serviceRank - b.serviceRank).map((assoc) => ({
      id: getRequiredObjectId(assoc.site),
    })) ?? [],
  }
}

export const SiteSiteAssignmentFormFields = (props: NonNullable<unknown>) => {
  const {} = props;

  const {t} = useTranslation(["translation", "common"]);
  const [opened, handlers] = useDisclosure(false);

  const form = useSiteSiteAssignmentFormContext()

  const selectedSiteIds = useMemo(() => {
    return form.values.normalSites.map((s, _) => {
      return s.id
    })
  }, [form.values.normalSites])

  const onSiteSelect = useCallback((sites: SiteEntity[] | undefined) => {
    sites?.forEach((s) => {
      const exists = selectedSiteIds.findIndex(id => s.id === id) >= 0
      if (!exists) {
        form.insertListItem('normalSites', {id: s.id})
        form.insertListItem("serviceSites", {id: s.id})
      }
    })
    handlers.close()
  }, [form, handlers, selectedSiteIds])

  const onSiteRemove = useCallback((idToRemove: string) => {
    let indexes: number[] = []

    form.values.normalSites.forEach((s, index) => {
      if (s.id === idToRemove) {
        indexes.push(index)
      }
    })

    indexes.forEach((index) => {
      form.removeListItem('normalSites', index)
    })

    indexes = []

    form.values.serviceSites.forEach((s, index) => {
      if (s.id === idToRemove) {
        indexes.push(index)
      }
    })

    indexes.forEach((index) => {
      form.removeListItem('serviceSites', index)
    })

  }, [form])

  const normalSiteFields = form.values.normalSites.map((s, index) => (
    <Draggable key={index} index={index} draggableId={index.toString()}>
      {(provided) => (
        <Group ref={provided.innerRef} mt="xs" {...provided.draggableProps}>
          <Center {...provided.dragHandleProps}>
            <IconGripVertical size="1.2rem"/>
          </Center>
          <SiteSelect
            {...form.getInputProps(`normalSites.${index}.id`)}
            disabled={true}/>
          <ActionIcon>
            <IconTrash size="1.2rem" onClick={() => onSiteRemove(s.id)}/>
          </ActionIcon>
        </Group>
      )}
    </Draggable>
  ));

  const serviceSiteFields = form.values.serviceSites.map((s, index) => (
    <Draggable key={index} index={index} draggableId={index.toString()}>
      {(provided) => (
        <Group ref={provided.innerRef} mt="xs" {...provided.draggableProps}>
          <Center {...provided.dragHandleProps}>
            <IconGripVertical size="1.2rem"/>
          </Center>
          <SiteSelect
            {...form.getInputProps(`serviceSites.${index}.id`)}
            disabled={true}/>
          <ActionIcon>
            <IconTrash size="1.2rem" onClick={() => onSiteRemove(s.id)}/>
          </ActionIcon>
        </Group>
      )}
    </Draggable>
  ));

  return (
    <>
      <SiteAssignmentStrategySelect
        label={t("common.entity.siteassignment.form.field.strategy", {ns: "common"})}
        placeholder={t<string>("common.entity.siteassignment.form.field.strategy.placeholder", {ns: "common"})}
        clearable
        withAsterisk
        required
        {...form.getInputProps("strategy")} />
      <Divider my="sm" variant="dotted"/>

      <Grid>
        <Grid.Col span={5}>
          <Text fw={700}>Normal Work Rank</Text>
          <DragDropContext
            onDragEnd={({destination, source}) =>
              form.reorderListItem('normalSites', {from: source.index, to: destination!.index})
            }
          >
            <Droppable droppableId="dnd-list" direction="vertical">
              {(provided) => (
                <div {...provided.droppableProps} ref={provided.innerRef}>
                  {normalSiteFields}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </Grid.Col>

        <Grid.Col span={1}>
          <Divider my="sm" variant="dotted" orientation="vertical"/>
        </Grid.Col>

        <Grid.Col span={5}>
          <Text fw={700}>Service Work Ranks</Text>
          <DragDropContext
            onDragEnd={({destination, source}) =>
              form.reorderListItem('serviceSites', {from: source.index, to: destination!.index})
            }
          >
            <Droppable droppableId="dnd-list" direction="vertical">
              {(provided) => (
                <div {...provided.droppableProps} ref={provided.innerRef}>
                  {serviceSiteFields}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </Grid.Col>
      </Grid>


      <Divider my="sm" variant="dotted"/>

      <Group position="center" mt="md">
        <Button onClick={handlers.open}>
          Add Site
        </Button>
      </Group>

      <SiteSelectModal
        select={{
          withinPortal: true,
        }}
        size="lg"
        opened={opened}
        onClose={handlers.close}
        onSubmit={onSiteSelect}/>
    </>
  );
};

export const getSiteSiteAssignmentFormValues = (values: SiteSiteAssignmentFormFieldValues): SiteSiteAssignmentFormValues => {
  const assocs: SiteAssignmentSiteAssocFormValues[] = []
  values.normalSites.forEach((ns, i) => {
    assocs.push({
      site: ns.id,
      normalRank: i,
      serviceRank: values.serviceSites.findIndex((ss) => ss.id === ns.id)
    })
  })

  return {
    strategy: toSiteAssignmentStrategy(values.strategy),
    assocs: assocs,
  }
}

export const SiteSiteAssignmentForm = (props: SiteSiteAssignmentFormProps) => {
  const {
    defaultValues,
    onSubmit,
    submitting = false
  } = props;

  const {t} = useTranslation(["translation", "common"]);

  const form = useSiteSiteAssignmentForm({
    initialValues: getDefaultSiteSiteAssignmentFormValues(defaultValues),
    validate: {}
  });

  const onFormSubmit = useCallback(async (values: typeof form.values) => {
    if (onSubmit) {
      const formValues = getSiteSiteAssignmentFormValues(values)
      onSubmit(formValues);
    }
  }, [form, onSubmit]);

  return (
    <SiteSiteAssignmentFormProvider form={form}>
      <form onSubmit={form.onSubmit(onFormSubmit)}>
        <SiteSiteAssignmentFormFields/>
        <Divider my="sm" variant="dotted"/>
        <Button type="submit" loading={submitting} fullWidth mt="xl">
          {t("common.action.submit", {ns: "common"})}
        </Button>
      </form>
    </SiteSiteAssignmentFormProvider>
  );
};
